import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { SmtpConfig, JobPec } from '../../types';
import BACKEND_URL from '../../config.js';

const STATO_BADGE: Record<string, string> = {
  BOZZA:      'bop-badge--neutral',
  IN_CORSO:   'bop-badge--info',
  COMPLETATO: 'bop-badge--success',
  ERRORE:     'bop-badge--danger',
};

export default function PecMassivaPage() {
  const navigate = useNavigate();

  const [smtpList, setSmtpList] = useState<SmtpConfig[]>([]);
  const [jobs,     setJobs]     = useState<JobPec[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [sending,  setSending]  = useState(false);
  const [error,    setError]    = useState('');
  const [msg,      setMsg]      = useState('');

  const {
    societaId,
    setSocietaId,
    listaSocieta: societa,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  const [smtpPecId,  setSmtpPecId]  = useState('');
  const [oggetto,    setOggetto]    = useState('');
  const [testo,      setTesto]      = useState('');
  const [allegati,   setAllegati]   = useState<FileList | null>(null);
  const [fileDest,   setFileDest]   = useState<File | null>(null);

  const allegatiRef = useRef<HTMLInputElement>(null);
  const fileDestRef = useRef<HTMLInputElement>(null);

  const loadJobs = () =>
    apiFetch<{ jobs: JobPec[] }>('/api/pec-massiva/jobs').then((r) => setJobs(r.jobs));

  useEffect(() => {
    if (!pronta) return;
    Promise.all([
      apiFetch<{ smtp: SmtpConfig[] }>('/api/pec-massiva/smtp'),
      apiFetch<{ jobs: JobPec[] }>('/api/pec-massiva/jobs'),
    ])
      .then(([smtpRes, jobsRes]) => {
        setSmtpList(smtpRes.smtp);
        setJobs(jobsRes.jobs);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [pronta]); // eslint-disable-line

  // Filtra SMTP per società selezionata
  const filteredSmtp = smtpList.filter(
    (s) => !societaId || s.societaId === null || s.societaId === Number(societaId)
  );

  const handleSocietaChange = (val: string) => {
    setSocietaId(val);
    setSmtpPecId('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!smtpPecId || !oggetto || !testo || !fileDest) {
      setError('SMTP, oggetto, testo e file destinatari sono obbligatori');
      return;
    }
    setSending(true); setError(''); setMsg('');

    const token = localStorage.getItem('token');
    const fd = new FormData();
    fd.append('societaId',       societaId);
    fd.append('smtpPecId',       smtpPecId);
    fd.append('oggetto',         oggetto);
    fd.append('testo',           testo);
    fd.append('fileDestinatari', fileDest);
    if (allegati) Array.from(allegati).forEach((f) => fd.append('allegati', f));

    try {
      const res = await fetch(`${BACKEND_URL}/api/pec-massiva/job`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token || ''}` },
        body: fd,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).error || `Errore ${res.status}`);
      }
      const data = await res.json();
      setMsg(`Job #${data.job.id} creato e avviato — ${data.job.totaleDestinatari} destinatari.`);
      // Reset form
      setOggetto(''); setTesto(''); setSmtpPecId('');
      setAllegati(null); setFileDest(null);
      if (allegatiRef.current)  allegatiRef.current.value  = '';
      if (fileDestRef.current)  fileDestRef.current.value  = '';
      loadJobs();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2>Email Massive</h2>
          <p>Invio email in blocco a destinatari multipli</p>
        </div>
      </div>

      {/* ── Form nuovo job ────────────────────────────────────────────── */}
      <div className="bop-card bop-pec-massiva__card-form">
        <div className="bop-pec-massiva__form-title">Nuovo invio</div>

        {error && <div className="inline-error">{error}</div>}
        {msg   && (
          <div className="bop-pec-massiva__success-msg">
            {msg}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="bop-form-row">
            {/* Società — visibile se multi-società */}
            {!isUnica && (
              <div className="bop-form-group">
                <label className="bop-label">Società</label>
                <select
                  className="bop-select"
                  value={societaId}
                  onChange={(e) => handleSocietaChange(e.target.value)}
                >
                  <option value="">— Tutte —</option>
                  {societa.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
                </select>
              </div>
            )}

            <div className="bop-form-group">
              <label className="bop-label">SMTP <span className="req">*</span></label>
              <select
                className="bop-select"
                value={smtpPecId}
                onChange={(e) => setSmtpPecId(e.target.value)}
                required
              >
                <option value="">— Seleziona —</option>
                {filteredSmtp.map((s) => (
                  <option key={s.id} value={s.id}>[{s.tipo}] {s.host} ({s.fromEmail})</option>
                ))}
              </select>
            </div>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Oggetto <span className="req">*</span></label>
            <input className="bop-input" value={oggetto} onChange={(e) => setOggetto(e.target.value)} required />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Testo <span className="req">*</span></label>
            <textarea
              className="bop-input bop-pec-massiva__textarea"
              rows={6}
              value={testo}
              onChange={(e) => setTesto(e.target.value)}
              required
            />
          </div>

          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">File destinatari (Excel / CSV) <span className="req">*</span></label>
              <input
                ref={fileDestRef}
                type="file"
                className="bop-input"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => setFileDest(e.target.files?.[0] || null)}
                required
              />
              <span className="bop-pec-massiva__hint">
                Colonna intestata "email", oppure prima colonna
              </span>
            </div>

            <div className="bop-form-group">
              <label className="bop-label">Allegati (opzionale)</label>
              <input
                ref={allegatiRef}
                type="file"
                className="bop-input"
                multiple
                onChange={(e) => setAllegati(e.target.files)}
              />
            </div>
          </div>

          <button type="submit" className="bop-btn bop-btn-primary" disabled={sending || loading}>
            {sending ? 'Creazione in corso…' : 'Crea e Avvia Job'}
          </button>
        </form>
      </div>

      {/* ── Lista job ─────────────────────────────────────────────────── */}
      <div className="bop-card">
        <div className="bop-pec-massiva__section-header">
          <strong className="bop-pec-massiva__section-title">Job recenti</strong>
        </div>
        <div className="bop-table-wrapper">
          {loading ? (
            <div className="inline-loading">Caricamento…</div>
          ) : jobs.length === 0 ? (
            <div className="bop-empty-state">Nessun job creato.</div>
          ) : (
            <table className="bop-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Oggetto</th>
                  <th>Società</th>
                  <th>Stato</th>
                  <th>Progresso</th>
                  <th>Data</th>
                  <th className="bop-pec-massiva__th-actions"></th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => {
                  const pct = j.totaleDestinatari > 0
                    ? Math.round((j.inviati / j.totaleDestinatari) * 100)
                    : 0;
                  return (
                    <tr key={j.id}>
                      <td className="bop-text-muted">{j.id}</td>
                      <td><strong>{j.oggetto}</strong></td>
                      <td className="bop-text-muted">{j.societaNome || '—'}</td>
                      <td>
                        <span className={`bop-badge ${STATO_BADGE[j.statoJob] ?? 'bop-badge--neutral'}`}>
                          {j.statoJob}
                        </span>
                      </td>
                      <td>
                        <div className="bop-pec-massiva__progress-wrapper">
                          <div className="bop-pec-massiva__progress-track">
                            {/* dynamic width */}
                            <div className="bop-pec-massiva__progress-fill" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="bop-pec-massiva__progress-text">
                            {j.inviati}/{j.totaleDestinatari}
                          </span>
                        </div>
                      </td>
                      <td className="bop-pec-massiva__date-cell">
                        {new Date(j.createdAt).toLocaleString('it-IT')}
                      </td>
                      <td>
                        <button
                          className="bop-btn bop-btn-ghost"
                          onClick={() => navigate(`/tecnico/pec-massiva/${j.id}`)}
                        >
                          Dettaglio
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
}
